import { useMemo, useState, useEffect } from 'react'
import { Type } from '@/components/common'
import { Campaign } from '@/services/api/campaign'
import useSiteAssets from '@/services/hooks/useSiteAssets'

interface Props {
  value: number
  dimension: string
  descriptor: string
  clarificationSymbol: string
  className?: string
}

const TimeRemaining: React.FC<Props> = ({
  value,
  dimension,
  descriptor,
  clarificationSymbol,
  className,
}) => {
  return (
    <Type
      as="p"
      variant="paragraph-lg"
      className={className}
      data-testid="TimeRemaining"
    >
      {(dimension === 'Hour' || dimension === 'Minute') && value === 1
        ? 'Less Than '
        : ''}
      {value} {dimension} {descriptor}
      {clarificationSymbol}
    </Type>
  )
}

interface WrapperProps {
  campaign: Campaign
  className?: string
  forceCountdown?: boolean
}

const TimeRemainingWrapper = ({
  campaign,
  className,
  forceCountdown,
}: WrapperProps) => {
  const { timeRemainingValue, timeRemainingDimension } = campaign.timeRemaining
  const clarificationSymbol = campaign.isSellingFast ? '†' : '*'
  const siteAssets = useSiteAssets()
  const [currentTime, setCurrentTime] = useState(new Date())

  const { value, dimension } = useMemo(() => {
    const closeDate = new Date(campaign.closeDate)
    const diffTime = Math.max(closeDate.getTime() - currentTime.getTime(), 0)
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60))
    const diffMinutes = Math.floor(diffTime / (1000 * 60))

    if (forceCountdown && diffMinutes < 60) {
      return {
        value: diffMinutes,
        dimension: diffMinutes === 1 ? 'Minute' : 'Minutes',
      }
    } else if (forceCountdown && diffHours <= 72) {
      return {
        value: diffHours,
        dimension: diffHours === 1 ? 'Hour' : 'Hours',
      }
    } else {
      return {
        value: timeRemainingValue,
        dimension: timeRemainingDimension,
      }
    }
  }, [
    campaign,
    timeRemainingValue,
    timeRemainingDimension,
    forceCountdown,
    currentTime,
  ])

  useEffect(() => {
    if (forceCountdown) {
      const timer = setInterval(() => {
        setCurrentTime(new Date())
      }, 60000) // Update every minute (60000ms)

      return () => clearInterval(timer)
    }
  }, [forceCountdown])

  return (
    <TimeRemaining
      value={value}
      dimension={dimension}
      descriptor={siteAssets?.timeRemainingDescriptor}
      clarificationSymbol={clarificationSymbol}
      className={className}
    />
  )
}

export default TimeRemainingWrapper
